/**
 * Chat Offline Store — read-cache de mensagens (Story 2.7.3, FR-X1-3).
 *
 * IndexedDB nativa com UM object store:
 *
 *   "messages" (keyPath: id)
 *     - histórico cacheado para leitura offline
 *     - rolling window de 50 por sala (purga a mais antiga ao cachear a 51ª)
 *
 * NOTA (Story X1.3.3): a antiga fila "outgoing" deste arquivo foi APOSENTADA
 * e substituída pela fila genérica `@/lib/offline-queue` (drenada na thread
 * principal — o dreno via Service Worker da 2.7.3 deixou de existir no X1.1.2).
 * A `DB_VERSION` subiu pra 2 e o `onupgradeneeded` remove o store "outgoing"
 * herdado. Este módulo agora cobre só o read-cache (offline reading).
 *
 * `clearAll()` é chamado no logout (AuthContext) — device compartilhado não
 * pode vazar histórico cacheado do usuário anterior.
 */

export const CHAT_OFFLINE_DB_NAME = "seweb-chat-offline";
export const CHAT_OFFLINE_DB_VERSION = 2;
export const STORE_MESSAGES = "messages";
/** Store legado removido na v2 — mantido como constante só pra migração. */
const STORE_OUTGOING_LEGACY = "outgoing";

export const ROLLING_WINDOW_PER_ROOM = 50;

export interface CachedMessage {
  id: string;
  room_id: string;
  author_id: string;
  content: unknown;
  created_at: string;
  parent_message_id?: string | null;
}

function isIndexedDBAvailable(): boolean {
  return typeof globalThis !== "undefined" && "indexedDB" in globalThis;
}

function openDb(): Promise<IDBDatabase> {
  if (!isIndexedDBAvailable()) {
    return Promise.reject(new Error("IndexedDB indisponível neste ambiente"));
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(CHAT_OFFLINE_DB_NAME, CHAT_OFFLINE_DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_MESSAGES)) {
        const messages = db.createObjectStore(STORE_MESSAGES, { keyPath: "id" });
        messages.createIndex("by-room", "room_id", { unique: false });
        messages.createIndex("by-time", "created_at", { unique: false });
      }
      // Migração v1→v2: remove a fila "outgoing" órfã (agora em offline-queue).
      if (db.objectStoreNames.contains(STORE_OUTGOING_LEGACY)) {
        db.deleteObjectStore(STORE_OUTGOING_LEGACY);
      }
    };

    // Outra aba segurando a v1 aberta bloquearia o upgrade v1→v2 — sem este
    // handler a Promise penduraria pra sempre.
    req.onblocked = () => reject(new Error("IndexedDB bloqueado (outra aba aberta?)"));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("Falha ao abrir IndexedDB"));
  });
}

function promisifyRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request error"));
  });
}

function promisifyTx(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB tx error"));
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB tx aborted"));
  });
}

/**
 * Persiste uma mensagem no cache E aplica rolling window (50/sala) na MESMA
 * transação readwrite. Combinar é crítico: split em 2 tx permitia que dois
 * `cacheMessage` concorrentes lessem o mesmo snapshot stale e ambos pulassem
 * o trim, deixando a sala com mais de 50 itens.
 */
export async function cacheMessage(message: CachedMessage): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_MESSAGES, "readwrite");
    const store = tx.objectStore(STORE_MESSAGES);

    store.put(message);

    const all = (await promisifyRequest(
      store.index("by-room").getAll(IDBKeyRange.only(message.room_id))
    )) as CachedMessage[];

    if (all.length > ROLLING_WINDOW_PER_ROOM) {
      all.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      for (const old of all.slice(ROLLING_WINDOW_PER_ROOM)) {
        store.delete(old.id);
      }
    }

    await promisifyTx(tx);
  } finally {
    db.close();
  }
}

export async function getCachedMessages(
  roomId: string,
  limit: number = ROLLING_WINDOW_PER_ROOM
): Promise<CachedMessage[]> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_MESSAGES, "readonly");
    const index = tx.objectStore(STORE_MESSAGES).index("by-room");
    const all = (await promisifyRequest(index.getAll(IDBKeyRange.only(roomId)))) as CachedMessage[];
    all.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    return all.slice(0, limit);
  } finally {
    db.close();
  }
}

/**
 * Esvazia o cache. SECURITY: chamar em logout — sem isso, o próximo usuário do
 * mesmo device vê mensagens cacheadas do anterior.
 */
export async function clearAll(): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE_MESSAGES, "readwrite");
    tx.objectStore(STORE_MESSAGES).clear();
    await promisifyTx(tx);
  } finally {
    db.close();
  }
}
